import ApiService from 'api-client/ApiService'
import config from 'app-config'
import {
  VMHAEventsResponse,
  VMHATaskDetailsResponse,
} from 'app/plugins/openstack/components/infrastructure/hosts/vmha/types'
import { getTimeRangeInEpochSeconds } from 'app/plugins/openstack/components/infrastructure/hosts/vmha/timeHelpers'

class VMHA extends ApiService {
  public getClassName() {
    return 'vmha'
  }

  protected async getEndpoint() {
    return Promise.resolve(config.apiHost)
  }

  get baseUrl(): string {
    return '/select/0/prometheus/api/v1'
  }

  /**
   * Fetches VMHA events generated in the last 1 day
   */
  getVMHAEvents = async (): Promise<VMHAEventsResponse> => {
    try {
      const { start, end } = getTimeRangeInEpochSeconds(1)
      const query = `query=vmha_events_generated&start=${start}&end=${end}&step=1m`
      const url = `${this.baseUrl}/query_range?${query}`

      // Set extractNestedData: false to preserve the full Prometheus response structure
      return await this.client.basicGet<VMHAEventsResponse>({
        url,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getVMHAEvents',
          extractNestedData: false,
        },
      })
    } catch (error) {
      // Fail silently as per requirements
      console.error('Failed to fetch VMHA events:', error)
      return {
        status: 'success',
        isPartial: false,
        data: {
          resultType: 'vector',
          result: [],
        },
        stats: {
          seriesFetched: '0',
          executionTimeMsec: 0,
        },
      }
    }
  }

  /**
   * Fetches VMHA events for a specific host in the last 30 days
   */
  getVMHAEventsForHost = async (
    hostId: string,
    start: number,
    end: number,
  ): Promise<VMHAEventsResponse> => {
    try {
      const query = `query=vmha_events_generated{host_id="${hostId}",phase="completed"}&start=${start}&end=${end}&step=1m`
      const url = `${this.baseUrl}/query_range?${query}`

      // Set extractNestedData: false to preserve the full Prometheus response structure
      return await this.client.basicGet<VMHAEventsResponse>({
        url,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getVMHAEventsForHost',
          extractNestedData: false,
        },
      })
    } catch (error) {
      // Fail silently as per requirements
      console.error(`Failed to fetch VMHA events for host ${hostId}:`, error)
      return {
        status: 'success',
        isPartial: false,
        data: {
          resultType: 'vector',
          result: [],
        },
        stats: {
          seriesFetched: '0',
          executionTimeMsec: 0,
        },
      }
    }
  }

  /**
   * Fetches task details for a specific VMHA event UUID
   */
  getVMHATaskDetails = async (
    vmhaUuid: string,
    timestamp: number,
  ): Promise<VMHATaskDetailsResponse> => {
    try {
      const query = `query=vmha_task_details{vmha_uuid="${vmhaUuid}"}&time=${timestamp}&step=1m`
      const url = `${this.baseUrl}/query?${query}`

      // Set extractNestedData: false to preserve the full Prometheus response structure
      return await this.client.basicGet<VMHATaskDetailsResponse>({
        url,
        options: {
          clsName: this.getClassName(),
          mthdName: 'getVMHATaskDetails',
          extractNestedData: false,
        },
      })
    } catch (error) {
      // Fail silently as per requirements
      console.error(`Failed to fetch VMHA task details for ${vmhaUuid}:`, error)
      return {
        status: 'success',
        isPartial: false,
        data: {
          resultType: 'vector',
          result: [],
        },
        stats: {
          seriesFetched: '0',
          executionTimeMsec: 0,
        },
      }
    }
  }
}

export default VMHA
